'use client'

import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/shared/data-table/data-table'
import { createUnifiedColumns } from './components/unifiedColumns'
import { FIND_UNIFIED_CUSTOMER_ACCOUNTS_AND_LOCATIONS } from './customerAccountQueries'
import { transformToUnifiedData } from './utils/transformUnifiedData'
import { UnifiedUserLocationRow, CustomerAccount } from './types'
import { CustomerSubAccount } from '../CustomerSubAccounts/types'
import { useParams } from 'react-router-dom'
import { Sheet } from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import CustomerAccountForm from './CustomerAccountForm'
import CustomerSubAccountForm from '../CustomerSubAccounts/CustomerSubAccountForm'
import { CustomerAccountActions } from './components/CustomerAccountActions'

interface UnifiedCustomerAccountTableProps {
  customerId?: number
}

export function UnifiedCustomerAccountTable({
  customerId: propCustomerId,
}: UnifiedCustomerAccountTableProps) {
  const { customerId: paramCustomerId } = useParams<{ customerId: string }>()
  const customerId = propCustomerId || parseInt(paramCustomerId!)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<CustomerAccount | null>(
    null
  )
  const [isLocationFormOpen, setIsLocationFormOpen] = useState(false)
  const [editingLocation, setEditingLocation] =
    useState<CustomerSubAccount | null>(null)
  const [selectedLocationForNewUser, setSelectedLocationForNewUser] =
    useState<CustomerSubAccount | null>(null)

  const { data, loading, error } = useQuery(
    FIND_UNIFIED_CUSTOMER_ACCOUNTS_AND_LOCATIONS,
    {
      variables: { customerId: customerId },
      skip: !customerId,
    }
  )

  console.log('UnifiedCustomerAccountTable data:', data)

  const handleEditAccount = (account: CustomerAccount) => {
    setEditingAccount(account)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingAccount(null)
    setSelectedLocationForNewUser(null)
  }

  const handleEditLocation = (location: CustomerSubAccount) => {
    console.log('handleEditLocation called with:', location)
    setEditingLocation(location)
    setIsLocationFormOpen(true)
  }

  const handleCloseLocationForm = () => {
    setIsLocationFormOpen(false)
    setEditingLocation(null)
  }

  const handleAddUser = () => {
    setEditingAccount(null)
    setIsFormOpen(true)
  }

  const handleAddLocation = () => {
    setEditingLocation(null)
    setIsLocationFormOpen(true)
  }

  const handleAddUserToLocation = (location: CustomerSubAccount) => {
    setEditingAccount(null)
    setSelectedLocationForNewUser(location)
    setIsFormOpen(true)
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="h-64 bg-gray-100 rounded animate-pulse" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400 text-xs">
          Error loading accounts: {error.message}
        </p>
      </div>
    )
  }

  const accounts = data?.findManyCustomerAccount || []
  const locations = data?.findManyCustomerSubAccount || []

  const unifiedData: UnifiedUserLocationRow[] = transformToUnifiedData(
    accounts,
    locations
  )

  // Create columns with edit handlers
  const columns = createUnifiedColumns(
    customerId,
    handleEditLocation,
    handleEditAccount,
    handleAddUserToLocation
  )

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold tracking-tight">
              User Accounts & Locations
            </h2>
            <p className="text-muted-foreground text-sm">
              Manage user accounts grouped by location. Users with assigned
              location can see and manage their location's orders
            </p>
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={handleAddUser} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
            <Button
              type="button"
              onClick={handleAddLocation}
              size="sm"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Location
            </Button>
          </div>
        </div>

        <DataTable columns={columns} data={unifiedData} defaultPageSize={10} />
      </div>

      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <CustomerAccountForm
          data={{
            id: editingAccount?.id,
            customerId,
            preSelectedLocationId: selectedLocationForNewUser?.id,
          }}
          onRequestClose={handleCloseForm}
        />
      </Sheet>

      <Sheet open={isLocationFormOpen} onOpenChange={setIsLocationFormOpen}>
        {isLocationFormOpen && (
          <CustomerSubAccountForm
            data={{
              id: editingLocation?.id,
              customerId,
            }}
            onRequestClose={handleCloseLocationForm}
          />
        )}
      </Sheet>
    </>
  )
}
